// BookDetails.tsx
import React, { useState, useEffect, ChangeEvent } from "react";
import axios from "axios";
import { useAuth } from '../../../global-components/src';
import { GoogleBooksItem } from "../../../types";
import { useMessage } from "../../../global-components/src/index";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./style.css"
import { READBOOKS_API_URL, READBOOKS_URL } from "../../../global-components/src/utils/vars";
import { censorText, clearText, errorMessage } from "../../../global-components/src/utils/text";
import Modal from "../../../global-components/src/components/modal/modal";
import { getGoogleBookImage } from "../../../utils/book";
import { Helmet } from "react-helmet-async";
import { BookOpenText } from "lucide-react";
import NotFound from "../../errors/NotFound";
import { isMyLove } from "../../../global-components/src/utils/auth";
import { BookPlaceholder } from "../../books/placeholder";
import { set } from "lodash";
import Spinner from "../../../global-components/src/components/spinner/spiner";

interface Form {
    paginas_lidas: number;
    data_inicio: string;
}

export default function GoogleBookDetails() {
    const [showAdd, setShowAdd] = useState(false);
    const [showShare, setShowShare] = useState(false);
    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState<Form| null>(null);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [loadingAdd, setLoadingAdd] = useState(false);
    const [book, setBook] = useState<GoogleBooksItem | null>(null);
    const [errorLoadData, setErrorLoadData] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [errorStatusCode, setErrorStatusCode] = useState<404 | null>(null);
    const { token, user } = useAuth();
    const [errorsInput, setErrorsInput] = useState<any>({});
    const { setMessage } = useMessage();
    const { id } = useParams();
    const [catalogBook, setCatalogBook] = useState<string | null>(null);
    const [loadingUploadPdf, setLoadingUploadPdf] = useState<boolean>(false);
    const navigate = useNavigate();
    const [processedText, setProcessedText] = useState<string>("");
    const shareUrl = `${READBOOKS_URL}/google-livros/${book?.id}`;

    const loadCatalogBook = async () => {
        try {
            const response = await axios.get(`${READBOOKS_API_URL}/catalog/${id}/read-url`, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            setCatalogBook(response.data.url);
        } catch (err) {
            console.log(err);
        }
    };

    const loadData = async () => {
        // Carrega data locais ANTES de buscar na API
        const stored = localStorage.getItem(`book-${id}`);
        if (stored) {
            const dataLocal = JSON.parse(stored);
            setCanEdit(dataLocal?.id_usuario === user?.id);
            setBook(dataLocal)
            setProcessedText(localStorage.getItem(`processedText-${id}`) ?? "")
        }
        
        let url = `${READBOOKS_API_URL}/google-books/${id}`;

        if (loading) return;
            setLoading(true);

        try {
            const response = await axios.get(url, {
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = response.data;
            setBook(data);
        } catch (err: any) {
            setErrorStatusCode(err.response?.status || 500);
            if(err.response?.status !== 404) {
                setLoading(false);
                const messageError = errorMessage(err);
                setMessage(messageError, "error");
                console.error("Erro ao pesquisar: ", messageError);
                console.error("Erro ao pesquisar: ", err);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if(id){
            loadData();
            loadCatalogBook();
        }
    }, [id]);

    const formatDate = (date?: string) =>date ? new Date(date).toLocaleDateString("pt-BR") : null;

    const copyLink = () => {
        navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
    };

    const handleUploadPdf = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || loadingUploadPdf) return;
        setLoadingUploadPdf(true);

        const formData = new FormData();
        formData.append("title", String(book?.volumeInfo.title));
        formData.append("type", "google_books");
        formData.append("google_book_id", String(book?.id));
        formData.append("total_pages", String(book?.volumeInfo.pageCount));
        formData.append("file", file);

        try {
            const response = await axios.post(
                `${READBOOKS_API_URL}/catalog/upload`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            setMessage("PDF enviado com sucesso!", "success");

            // atualiza estado para liberar leitura
            loadCatalogBook();
        } catch (err: any) {
            setMessage(err?.response?.data?.message || "Erro ao enviar PDF", "error");
        } finally {
            setLoadingUploadPdf(false);
        }
    };

    

    const handleChange = (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
      const target = e.target as HTMLInputElement | HTMLTextAreaElement;
      const { name, value, type } = target;
      const checked = (target as HTMLInputElement).checked;
    
      setForm((prev) => {
          if(name == "paginas_lidas" && !isNaN(Number(book?.volumeInfo.pageCount)) && Number(value) > Number(book?.volumeInfo.pageCount)) {
              setErrorsInput((prevErrors: any) => ({
                  ...prevErrors,
                  paginas_lidas: [`O número de páginas lidas não pode ser maior que ${book?.volumeInfo.pageCount}.`],
              }));
              return prev;
          } else {
              setErrorsInput((prevErrors: any) => ({
                    ...prevErrors,
                    [name]: null,
                }));
            }

        const updated = {
          ...(prev ?? {
            paginas_lidas: 0,
            data_inicio: "",
          }),
          [name]: type === "checkbox" ? checked : value,
        };

    
        return updated;
      });
    };

    const handleSubmit = async () => {
        if(loadingAdd) return;
        setLoadingAdd(true);

        try {
            let errors: Record<string, string[]> = {};
      
            if (form?.paginas_lidas == 0) {
                errors.nome_livro = ["A quantidade de páginas lidas não pode ser zero."];
            }
            if (form?.data_inicio && new Date(form.data_inicio).getTime() > Date.now()) {
                errors.data_inicio = ["A data de início não pode ser no futuro."];
            }
           
            if (Object.keys(errors).length > 0) {
                throw {
                    response: {
                        data: {
                            errors
                        }
                    }
                };
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            };

            const payload = {
                thumbnail: book ? getGoogleBookImage(book) : null,
                name: book?.volumeInfo.title,
                description: book?.volumeInfo.description,
                total_pages: book?.volumeInfo.pageCount,
                pages_read: form?.paginas_lidas,
                time_read: form?.data_inicio && new Date(form.data_inicio).getTime() < Date.now() ? `${Math.floor((Date.now() - new Date(form.data_inicio).getTime()) / (1000 * 60 * 60 * 24))} dias` : "0 dias",
                start_date: form?.data_inicio,
                is_read: !isNaN(Number(book?.volumeInfo.pageCount)) && !isNaN(Number(form?.paginas_lidas)) && Number(book?.volumeInfo.pageCount) === Number(form?.paginas_lidas),
            };

            const url = `${READBOOKS_API_URL}/google-books/add-to-read/${id}`;
            const response = await axios.post(url, payload, config);
            if (user?.id) {
                localStorage.removeItem(`ai_reading_analysis_${user.id}`);
            }
            localStorage.removeItem("ai_reading_analysis");
            setShowAdd(false);
            setMessage(response.data.message, "success");
            navigate(`/livros/${response.data.book.uuid}`);
        } catch (err: any) {
            if (err?.response?.data.errors) {
                console.error(err?.response?.data.errors);
                setErrorsInput(err.response.data.errors); // Atualiza o estado com os erros
                return;
            }
            
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error("Erro ao pesquisar: ", messageError);
            console.error("Erro ao pesquisar: ", err);
        } finally {
        setLoadingAdd(false);
        }
    };

    if (errorStatusCode === 404) {
        return (
            <NotFound text="Livro não encontrado" showLink={false}>
                <BookOpenText className="icon-empty"/>
            </NotFound>
        );
    }

    return (
        <div className="margin-top-menu container container-livro">
            <Modal
                textBtnConfirmar="Ok"
                idModal="modal-share"
                coverMobile={false}
                onOpen={copyLink}
                onClose={() => setShowShare(false)}
                title={`Link copiado${isMyLove(user) ? ", meu amor" : ""}!`} 
                text={`O link do livro foi copiado${isMyLove(user) ? ", meu lovezinho" : ""}: ${shareUrl}`}
                show={showShare}
                handleConfirm={() => {setShowShare(false);}}
                isLoading={loadingDelete}
            />

            <Modal
                textBtnConfirmar="Adicionar"
                idModal="modal-add"
                onOpen={copyLink}
                onClose={() => setShowAdd(false)}
                title={`Adicionar livro${isMyLove(user) ? ", meu amor" : ""}`} 
                text={`Deixe páginas lidas em branco para adicionar a lista de desejos${isMyLove(user) ? ", meu lovezinho" : ""}`}
                show={showAdd}
                handleConfirm={handleSubmit}
                isLoading={loadingAdd}
                textBtnCancelar="Cancelar"
                extraContent={
                    <>
                        <div className="mb-3">
                            <label htmlFor="paginas_lidas" className="label">Páginas lidas</label>
                            <input disabled={errorLoadData} onChange={handleChange} type="number" className={`form-control ${errorsInput.paginas_lidas ? 'is-invalid' : ''}`} id="paginas_lidas" name="paginas_lidas" value={form?.paginas_lidas} />
                            {errorsInput.paginas_lidas && (
                                <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.paginas_lidas[0]}</div> // Exibe a mensagem de erro
                            )}
                        </div>
                        {form?.paginas_lidas && form.paginas_lidas > 0 && (
                            <div className="mb-4">
                                <label htmlFor="data_inicio" className="label">Data de início</label>
                                <input disabled={errorLoadData} onChange={handleChange} type="date" className={`form-control ${errorsInput.data_inicio ? 'is-invalid' : ''}`} id="data_inicio" name="data_inicio" value={form?.data_inicio} />
                                {errorsInput.data_inicio && (
                                    <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.data_inicio[0]}</div> // Exibe a mensagem de erro
                                )}
                            </div>    
                        
                        )}
                    </>
                }/>

            <div className="row">
                <div className="col-4">
                    {loading && !book ? (
                        <BookPlaceholder className="img-livro-selecionado"/>
                    ) :(
                        <>
                        <div className={`div-img-livro-selecionado ${
                        book && getGoogleBookImage(book, true) === "img/book_transparent.png"
                            ? "img-capa-default"
                            : ""
                        }`}
                        style={
                        book && getGoogleBookImage(book, true) === "../img/book_transparent.png"
                            ? { backgroundImage: `url(${getGoogleBookImage(book, true)})` }
                            : {}
                        }
                    >
                        {book && getGoogleBookImage(book, true) !== "../img/book_transparent.png" ? (
                        <img
                            onError={(e) => { e.currentTarget.src = "/img/book_transparent.png"; }}
                            src={getGoogleBookImage(book, true)}
                            alt="Capa do livro"
                            className="img-livro-selecionado"
                        />
                        ) : (
                        <h1>{book?.volumeInfo.title}</h1>
                        )}
                    </div>
                        </>
                    )}
                </div>

                {!loading || book ? (
                    <div className="col-8 col-info-livro">
                        <h1 className="nome-livro-lido">{book?.volumeInfo.title}</h1>

                        <div className="row row-livro-lido">
                            <div className="col">
                                <p>
                                    <b>Autores:</b> {book?.volumeInfo.authors?.length ? book?.volumeInfo.authors.join(", ") : "Desconhecido"} <br />
                                    <b>Páginas:</b> {book?.volumeInfo.pageCount ? book?.volumeInfo.pageCount : "Desconhecido"} <br />
                                </p>
                                <p>
                                    <b>Publicado em:</b> {formatDate(book?.volumeInfo.publishedDate) || "Desconhecido"} <br />
                                    <b>Editora:</b> {book?.volumeInfo.publisher || "Desconhecido"} <br />
                                    <b>ISBN:</b> {book?.volumeInfo.industryIdentifiers?.map((id) => id.identifier).join(", ") || "Desconhecido"} <br />
                                    <b>Idioma:</b> {book?.volumeInfo.language || "Desconhecido"} <br />
                                    <b>Link:</b> <a href={book?.volumeInfo.infoLink} target="_blank" rel="noopener noreferrer">Google Livros</a>
                                </p>
                                <p>
                                    <b>Classificação:</b> {book?.volumeInfo.averageRating ? `${book?.volumeInfo.averageRating} (${book?.volumeInfo.ratingsCount || 0} avaliações)` : "Sem avaliações"} <br />
                                    <b>Link de compra:</b> {book?.saleInfo.buyLink ? <a href={book?.saleInfo.buyLink} target="_blank" rel="noopener noreferrer">Comprar</a> : "Não disponível"} <br />
                                    <b>Link de pré-visualização:</b> {book?.volumeInfo.previewLink ? <a href={book?.volumeInfo.previewLink} target="_blank" rel="noopener noreferrer">Pré-visualizar</a> : "Não disponível"} <br />
                                    <b>Data de adição:</b> {book?.volumeInfo.publishedDate ? formatDate(book?.volumeInfo.publishedDate) : "Desconhecida"} <br />
                                    <b>Data de modificação:</b> {book?.volumeInfo.publishedDate ? formatDate(book?.volumeInfo.publishedDate) : "Desconhecida"} <br />
                                </p>
                            </div>
                            <div className="col col-btn-google-book">
                                <button onClick={(() => { setShowAdd(true); })} type="submit" className="btn-livro btn btn-primary" style={{width: "100%", marginTop: "10px"}} >
                                    Adicionar a leitura
                                </button>
                                {!catalogBook ? (
                                    <>
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            id="upload-pdf-input"
                                            style={{ display: "none" }}
                                            onChange={handleUploadPdf}
                                        />

                                        <button
                                            className="btn-livro btn btn-warning"
                                            style={{ width: "100%", marginTop: "10px" }}
                                            onClick={() => document.getElementById("upload-pdf-input")?.click()}
                                        >
                                            {loadingUploadPdf ? <Spinner/> : "Enviar PDF do livro"}
                                        </button>
                                        <p style={{ marginBottom: 5, marginTop: 2, opacity: .9 }}>Envie o PDF do livro para outros usuários lerem</p>
                                    </>
                                ) : (
                                    <>
                                        <button disabled className="btn-livro btn btn-success" style={{ width: "100%", marginTop: "10px" }}>
                                            Ler agora
                                        </button>
                                        <p style={{ marginBottom: 7, marginTop: 2 }}>Adicione a leitura para acessar o PDF</p>
                                    </>
                                )}
                                <button onClick={() => setShowShare(true)} type="submit" className="btn-livro btn btn-outline-primary" style={{width: "100%", marginTop: "10px"}} >
                                    Compartilhar
                                </button>
                            </div>

                            <p dangerouslySetInnerHTML={{ __html: clearText(book?.volumeInfo.description || "", false) }}></p>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
}
